/*
  Horizon cart adapter -- window.CosneticHorizonCart.

  Horizon's cart drawer, header bubble and sticky bar do not react to a plain fetch to
  cart/add.js: they listen for CartLinesUpdateEvent (from the '@shopify/events' import-map
  module) and await its promise, which must resolve with the new cart and the re-rendered
  sections. The old theme's 'cart:prepare-bundled-sections' / 'cart:change' events have no
  listener in V3 -- so the builder and the card "+" added items to the cart while nothing on the
  page moved (drawer shut, bubble unchanged): "add to cart doesn't work" (owner, 2026-09-21).

  This mirrors assets/product-form.js exactly: collect the cart-items section ids, dispatch the
  event with a deferred promise, POST, re-read cart.js, resolve. `action: 'add'` is what makes
  the drawer auto-open. Used by the card "+" below and by assets/subrewards.js.
*/
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const sectionIds = () => [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId).filter(Boolean);
  const cartJson = async () => (await fetch(`${ROOT}cart.js`)).json();
  let eventsModule = null;
  const events = async () => (eventsModule ||= await import('@shopify/events'));

  const run = async ({ action, context, lines, path, body, source }) => {
    const { CartLinesUpdateEvent } = await events();
    const ids = sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    (source || document.body).dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));
    const itemCount = lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);
    let res, json;
    try {
      res = await fetch(`${ROOT}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ ...body, sections: ids.join(','), sections_url: window.location.pathname }),
      });
      json = await res.json();
    } catch (error) { json = { status: 500, description: String(error) }; res = { ok: false }; }
    const ajax = await cartJson();
    const failed = !res.ok || json.status;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajax),
      detail: { items: ajax.items, source: 'cosnetic', sourceId: 'cosnetic', itemCount, sections: json.sections, didError: !!failed },
    });
    if (failed) throw new Error(json.description || json.message || 'Cart request failed');
    ajax.sections = json.sections;
    return ajax;
  };

  window.CosneticHorizonCart = {
    add: (items, source) => run({
      action: 'add', context: 'product', source, path: 'cart/add.js', body: { items },
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.quantity) || 1 })),
    }),
    change: (body, source) => run({
      action: Number(body.quantity) > 0 ? 'update' : 'remove', context: 'cart', source, path: 'cart/change.js', body,
      lines: [{ id: String(body.id || body.line || ''), quantity: Number(body.quantity) || 0 }],
    }),
  };
})();

/*
  "+" quick add on product cards (snippets/cc-card.liquid). Goes through the
  Horizon cart adapter above so the drawer opens and the bubble updates. Only single-variant
  products get a <button data-cc-add>; products with lengths link to their page.
*/
(() => {
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cc-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      await window.CosneticHorizonCart.add([{ id: Number(button.dataset.ccAdd), quantity: 1 }], button);
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1200);
    } catch (error) {
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 1500);
      console.warn('[cosnetic-ui] quick add failed', error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
})();

/*
  Phone product gallery (snippets/cc-mobile-gallery.liquid). The track is native scroll-snap,
  so swiping works with no JS; this only keeps the arrows and thumbnails in step with it.
*/
(() => {
  const init = (root) => {
    const track = root.querySelector('[data-cc-mg-track]');
    const slides = [...track.children];
    const thumbs = [...root.querySelectorAll('[data-cc-mg-go]')];
    const prev = root.querySelector('[data-cc-mg-prev]');
    const next = root.querySelector('[data-cc-mg-next]');
    if (!slides.length) return;

    const step = () => slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
    const current = () => Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / step())));
    const go = (i) => track.scrollTo({ left: i * step(), behavior: 'smooth' });

    const sync = () => {
      const i = current();
      thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
      if (prev) prev.hidden = i === 0;
      if (next) next.hidden = i === slides.length - 1;
      // keep the current thumb in view by scrolling the thumb row only -- scrollIntoView would
      // also scroll the page vertically, which on load jumps the shopper down to the thumbs
      const t = thumbs[i], row = t && t.parentElement;
      if (t && row) {
        const l = t.offsetLeft - row.offsetLeft, r = l + t.offsetWidth;
        if (l < row.scrollLeft) row.scrollLeft = l - 16;
        else if (r > row.scrollLeft + row.clientWidth) row.scrollLeft = r - row.clientWidth + 16;
      }
    };

    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(sync); }, { passive: true });
    if (prev) prev.addEventListener('click', () => go(current() - 1));
    if (next) next.addEventListener('click', () => go(current() + 1));
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.ccMgGo))));
    sync();
  };
  const boot = () => document.querySelectorAll('[data-cc-mg]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/*
  UGC video row (sections/cc-ugc.liquid). Videos are preload="none": a tile starts loading
  and playing only when it comes within 200px of the viewport, and pauses when it leaves, so the
  row costs nothing on first paint. Tapping a tile toggles its sound (autoplay requires muted).
*/
(() => {
  const init = (root) => {
    const track = root.querySelector('[data-cc-ugc-track]');
    const videos = [...root.querySelectorAll('video')];
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) { v.preload = 'auto'; v.play().catch(() => {}); } else { v.pause(); }
      }), { rootMargin: '200px' });
      videos.forEach((v) => io.observe(v));
    }
    root.querySelectorAll('.cc-ugc__tile').forEach((tile) => tile.addEventListener('click', () => {
      const v = tile.querySelector('video'); if (!v) return;
      v.muted = !v.muted; tile.classList.toggle('is-audible', !v.muted);
      if (v.paused) v.play().catch(() => {});
    }));
    const by = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
    const prev = root.querySelector('[data-cc-ugc-prev]'), next = root.querySelector('[data-cc-ugc-next]');
    if (prev) prev.addEventListener('click', () => by(-1));
    if (next) next.addEventListener('click', () => by(1));
  };
  const boot = () => document.querySelectorAll('[data-cc-ugc]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* Recommendations (sections/cc-recommendations.liquid): fetch the rendered section and swap
   it in; an empty result removes the shell so no heading hangs over nothing. */
(() => {
  const load = async (el) => {
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cc-recs]');
      if (fresh && fresh.querySelector('.cc-grid')) el.innerHTML = fresh.innerHTML; else el.remove();
    } catch (e) { el.remove(); }
  };
  const boot = () => document.querySelectorAll('[data-cc-recs]').forEach((el) => { if (!el.querySelector('.cc-grid')) load(el); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* Menu drawer (snippets/cc-menu-drawer.liquid): the header's menu button opens our <dialog>
   instead of Horizon's drawer. Capture phase, so Horizon's own handler never runs. Closes on
   the x, a backdrop tap, or Escape (native to <dialog>). */
(() => {
  document.addEventListener('click', (event) => {
    const menu = document.getElementById('cc-menu');
    if (!menu) return;
    const trigger = event.target.closest('.header__drawer button, .header__drawer summary, [aria-controls*="menu-drawer"]');
    if (trigger) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!menu.open) menu.showModal();
      return;
    }
    if (event.target.closest('[data-cc-menu-close]') || event.target === menu) menu.close();
  }, true);
})();

/* Swipeable card photos: the pager thumb follows the scroll; arrows step one photo (mouse). */
(() => {
  const sync = (box) => {
    const t = box.querySelector('[data-cc-swipe-track]'); const thumb = box.querySelector('[data-cc-swipe-thumb]');
    if (!t || !thumb) return;
    const max = t.scrollWidth - t.clientWidth; const p = max > 0 ? t.scrollLeft / max : 0;
    const n = t.children.length;
    thumb.style.transform = `translateX(${p * (n - 1) * 100}%)`;
    const prev = box.querySelector('[data-cc-swipe-prev]'); const next = box.querySelector('[data-cc-swipe-next]');
    if (prev) prev.hidden = t.scrollLeft < 4;
    if (next) next.hidden = t.scrollLeft > max - 4;
  };
  document.addEventListener('scroll', (e) => {
    const t = e.target; if (!(t instanceof Element) || !t.matches('[data-cc-swipe-track]')) return;
    sync(t.closest('[data-cc-swipe]'));
  }, true);
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cc-swipe-prev], [data-cc-swipe-next]'); if (!b) return;
    e.preventDefault();
    const t = b.closest('[data-cc-swipe]').querySelector('[data-cc-swipe-track]');
    t.scrollBy({ left: (b.hasAttribute('data-cc-swipe-next') ? 1 : -1) * t.clientWidth, behavior: 'smooth' });
  });
})();


/* Sticky checkout on the cart page (phones): mirrors the total + checkout button in a slim bar that
   only shows while the real checkout area is scrolled out of view. Clicking it presses the real button,
   so checkout behaviour (selling plans, apps) is exactly Shopify's. */
(() => {
  const init = () => {
    const ctas = document.querySelector('.cart-page .cart__ctas');
    const real = ctas && ctas.querySelector('.cart__checkout-button');
    if (!real || document.querySelector('.cc-sticky-cta')) return;
    const bar = document.createElement('div');
    bar.className = 'cc-sticky-cta';
    bar.innerHTML = '<span class="cc-sticky-cta__total"><small></small><b></b></span><button type="button"></button>';
    document.body.appendChild(bar);
    const sync = () => {
      bar.querySelector('small').textContent = (document.querySelector('.cart-page .cart-totals__total-label') || {}).textContent || '';
      bar.querySelector('b').textContent = ((document.querySelector('.cart-page .cart-totals__total-value') || {}).textContent || '').trim();
      bar.querySelector('button').textContent = (real.textContent || '').trim();
    };
    sync();
    bar.querySelector('button').addEventListener('click', () => (document.querySelector('.cart-page .cart__checkout-button') || real).click());
    new IntersectionObserver(([e]) => bar.classList.toggle('is-on', !e.isIntersecting && !!document.querySelector('.cart-page .cart__checkout-button')))
      .observe(ctas);
    new MutationObserver(sync).observe(document.querySelector('.cart-page') || document.body, { subtree: true, childList: true, characterData: true });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();

/* Cart drawer add-ons: "+" adds the accessory (on the cart's lash delivery plan when it has one). */
document.addEventListener('click', async (e) => {
  const b = e.target.closest('[data-cc-addon]');
  if (!b || b.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  b.setAttribute('aria-busy', 'true');
  const item = { id: Number(b.dataset.ccAddon), quantity: 1 };
  if (b.dataset.plan) item.selling_plan = Number(b.dataset.plan);
  try {
    if (window.CosneticHorizonCart) await window.CosneticHorizonCart.add([item], b);   // source = the clicked element (the event is dispatched from it)
    else { await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [item] }) }); location.reload(); }
  } catch (err) { console.warn('[cosnetic-ui] add-on failed', err); }
  finally { b.removeAttribute('aria-busy'); }
});

/* Sticky buy bar on product pages (phones), rebuilt 2026-10-08: a full-width black button carrying the
   next action (mirrors the main buy button: the box builder's CTA, the starter set's "Vælg dine designs" /
   add button, or the theme's add-to-cart) and presses it, so builders, selling plans and apps behave as
   usual. Under it a trust row from <template id="cc-sticky-trust"> (snippets/cc-sticky-trust.liquid):
   guarantee or return line + real UGC avatars + the Trustpilot review count. */
(() => {
  // Split test "buybox" arm C has its own button; the arm can arrive after load (cookie consent), so rebind then.
  const find = () => document.querySelector('.product-details [data-cc-tiers-cta]') || document.querySelector('html.cx-buybox-c .subrewards [data-sr-c-cta]') || document.querySelector('.subrewards--size[data-mode="subscription"] [data-sr-s-cta]') || document.querySelector('.subrewards .sr-cta') || document.querySelector('.product-details [data-cc-kit-open]') || document.querySelector('.product-details .add-to-cart-button, .product-details [name="add"]');
  let bound = null;
  let watchers = [];
  const init = () => {
    const real = find();
    if (!real || real === bound) return;
    watchers.forEach((w) => w.disconnect());
    watchers = [];
    document.querySelectorAll('.cc-pdp-sticky').forEach((el) => el.remove());
    bound = real;
    const bar = document.createElement('div');
    bar.className = 'cc-pdp-sticky';
    bar.innerHTML = '<button type="button" class="cc-pdp-sticky__btn"></button>';
    const trust = document.getElementById('cc-sticky-trust');
    if (trust) bar.append(trust.content.cloneNode(true));
    document.body.appendChild(bar);
    const btn = bar.querySelector('.cc-pdp-sticky__btn');
    const sync = () => {
      const label = real.querySelector('[data-sr-pdp-cta-label], [data-sr-c-cta-label], [data-sr-s-cta-label]') || real;
      btn.textContent = (label.textContent || '').replace(/\s+/g, ' ').trim();
      btn.disabled = !!real.disabled && !real.matches('[data-cc-tiers-cta]');
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(real, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
    btn.addEventListener('click', () => real.click());
    // Show only once the real button has scrolled ABOVE the viewport (not before the shopper reaches it).
    const io = new IntersectionObserver(([en]) => bar.classList.toggle('is-on', !en.isIntersecting && en.boundingClientRect.top < 0));
    io.observe(real);
    watchers = [mo, io];
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
  document.addEventListener('cx-x:change', () => { if (document.readyState !== 'loading') init(); });
  document.addEventListener('sr:mode', init); // box size picker: subscription and one-time have different buttons
})();

/*
  Desktop PDP: the product column is sticky (Horizon `sticky-content--desktop`, top = header).
  Once the box picker made it taller than the screen, its add-to-cart sat below the fold and only
  came into view after every gallery image had scrolled past (owner, 2026-11-03). When the column is
  taller than the viewport, pin it by its bottom instead (negative top): it scrolls with the page
  until the button shows, then sticks there while the gallery carries on.
*/
(() => {
  const mq = window.matchMedia('(min-width: 750px)');
  const fit = () => {
    document.querySelectorAll('.product-details.sticky-content--desktop').forEach((el) => {
      if (!mq.matches) { el.style.top = ''; return; }
      const head = parseFloat(getComputedStyle(el).getPropertyValue('--sticky-header-offset')) || 0;
      const room = window.innerHeight - head;
      el.style.top = el.offsetHeight > room ? `${Math.round(window.innerHeight - el.offsetHeight - 24)}px` : '';
    });
  };
  const init = () => {
    const ro = new ResizeObserver(fit);
    document.querySelectorAll('.product-details.sticky-content--desktop').forEach((el) => ro.observe(el));
    window.addEventListener('resize', fit, { passive: true });
    mq.addEventListener('change', fit);
    fit();
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
